import type { Game } from '@/core/game';
import { activeCase } from '@/systems/cases';
import { patrollers, watcherFloor, watcherPos } from '@/systems/watchers';
import { FLOOR_COLOR, floorKind } from '@/world/floorBuilder';
import { floorColorNew } from '@/world/palette';
import { isNewStyle } from './artStyle';
import { tr } from '@/i18n';

/** 2D 平面圖：分區顏色、設備、門、玩家、目標、巡場的人 */
export function drawMinimap(cv: HTMLCanvasElement, g: Game, big = false): void {
  const rt = g.stage.rt; if (!rt || !g.day) return;
  const x = cv.getContext('2d'); if (!x) return;
  const [W, D] = rt.floor.size;
  const s = Math.min(cv.width / W, cv.height / D);
  const ox = (cv.width - W * s) / 2, oz = (cv.height - D * s) / 2;
  const X = (v: number) => ox + (v + W / 2) * s, Z = (v: number) => oz + (v + D / 2) * s;
  x.clearRect(0, 0, cv.width, cv.height);
  x.fillStyle = isNewStyle() ? 'rgba(46,38,33,0.92)' : 'rgba(14,22,27,0.9)'; x.fillRect(0, 0, cv.width, cv.height);
  if (g.stage.dark) { x.fillStyle = '#888'; x.font = '700 13px sans-serif'; x.fillText(tr("（太暗了，看不清楚）"), 10, cv.height / 2); return; }
  for (const z of rt.floor.zones) {
    if (z.hidden && !g.profile.flags.found5F) continue;
    x.fillStyle = (floorColorNew(floorKind(z)) ?? FLOOR_COLOR[floorKind(z)]) + (z.level < 0 && !z.outdoor ? '40' : '99');
    x.fillRect(X(z.rect[0]), Z(z.rect[1]), z.rect[2] * s, z.rect[3] * s);
    if (big) { x.fillStyle = '#e8eef0'; x.font = `600 ${Math.max(10, s * 0.5)}px sans-serif`; x.fillText(z.name.split(tr("（"))[0].trim(), X(z.rect[0]) + 4, Z(z.rect[1]) + 14); }
  }
  x.fillStyle = '#c4ccd0';
  for (const b of rt.solids) x.fillRect(X(b.minX), Z(b.minZ), (b.maxX - b.minX) * s, (b.maxZ - b.minZ) * s);
  for (const { data: d } of rt.doors.values()) {
    if (d.type === 'hidden' && !g.profile.flags[d.flag ?? '']) continue;
    x.fillStyle = d.type === 'airshower' ? '#5aa9e6' : d.type === 'oneway' ? '#5fb38a' : d.type === 'exit' ? '#e0a35c' : d.type === 'locked' ? '#d9534a' : '#9aa7ad';
    x.beginPath(); x.arc(X(d.pos[0]), Z(d.pos[1]), Math.max(3, s * 0.35), 0, Math.PI * 2); x.fill();
  }
  if (big) {
    x.fillStyle = '#e8eef0'; x.font = `500 ${Math.max(9, s * 0.38)}px sans-serif`; x.textAlign = 'center';
    for (const e of rt.equipment.values()) if (e.data.kind !== 'floorZone') x.fillText(e.data.name.split(/[・（(]| · /)[0].trim(), X(e.data.pos[0]), Z(e.data.pos[1]) + 4);
    x.textAlign = 'start';
  }
  for (const it of g.day.ground) if (it.floor === g.day.floor) { x.fillStyle = '#ffffffaa'; x.fillRect(X(it.pos[0]) - 2, Z(it.pos[1]) - 2, 4, 4); }
  const rat = g.day.rat; if (rat && rat.floor === g.day.floor && g.day.flags.ratRadar) { x.fillStyle = '#ff5a5a'; x.beginPath(); x.arc(X(rat.pos[0]), Z(rat.pos[1]), big ? 7 : 4, 0, Math.PI * 2); x.fill(); x.font = `${big ? 16 : 10}px sans-serif`; x.textAlign = 'center'; x.fillText('🐀', X(rat.pos[0]), Z(rat.pos[1]) - 6); }
  const k = activeCase(g);
  if (k && k.floor === g.day.floor) {
    const stg = g.day.cases[k.id].stage;
    let tx: number | null = null, tz: number | null = null;
    if (stg === 'inspect') { const e = rt.equipment.get(k.equipment); if (e) { tx = e.data.pos[0]; tz = e.data.pos[1]; } }
    else { const a = g.stage.actors.find(a => a.caseId === k.id); if (a) { tx = a.group.position.x; tz = a.group.position.z; } }
    if (tx != null && tz != null) { x.fillStyle = '#f2b134'; x.beginPath(); x.arc(X(tx), Z(tz), Math.max(4, s * 0.45), 0, Math.PI * 2); x.fill(); }
  }
  for (const n of patrollers(g)) {
    const w = watcherPos.get(n.id); if (!w || w.floor !== g.day.floor || watcherFloor(g, n) !== g.day.floor) continue;
    x.fillStyle = '#e06c7533'; x.beginPath(); x.arc(X(w.x), Z(w.z), g.c.game.watchers.visionRadius * s, 0, Math.PI * 2); x.fill();
    x.fillStyle = n.id === 'resident' ? '#e06c75' : '#5fb38a'; x.beginPath(); x.arc(X(w.x), Z(w.z), Math.max(3, s * 0.35), 0, Math.PI * 2); x.fill();
  }
  const [px, pz] = g.playerPos();
  x.fillStyle = '#ffffff'; x.strokeStyle = '#3f78a8'; x.lineWidth = 2;
  x.beginPath(); x.arc(X(px), Z(pz), Math.max(4, s * 0.4), 0, Math.PI * 2); x.fill(); x.stroke();
  x.fillStyle = '#e8eef0'; x.font = '700 12px sans-serif'; x.fillText(g.day.floor, 6, 14);
  if (k && k.floor !== g.day.floor) { x.fillStyle = '#f2b134'; x.fillText(tr("目標在 {0}", k.floor), 6, cv.height - 6); }
}
